import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, ChevronDown, ChevronLeft, ChevronRight, Check, Truck, Shield, RefreshCw, Clock } from 'lucide-react';
import Footer from '../components/Footer';
import ConfigurationForm from '../components/ConfigurationForm';
import CartModal from '../components/CartModal';
import SEOHead from '../components/SEO/SEOHead';
import { useCart } from '../context/CartContext';
import { products, navCategories } from '../mockData';
import axios from 'axios';
import Logo from '../components/Logo';
import { formatPrice } from '../utils/priceFormatter';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, getCartCount } = useCart();
  const [product, setProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activePictoIndex, setActivePictoIndex] = useState(null); // null = image principale, 0-5 = picto actif
  const [selectedTab, setSelectedTab] = useState('description');
  const [configuration, setConfiguration] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);
  const [cartItem, setCartItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricePerM2, setPricePerM2] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(true);
  const [minDeliveryCost, setMinDeliveryCost] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Détecter le type de produit depuis l'URL
  const searchParams = new URLSearchParams(window.location.search);
  const productType = searchParams.get('type');
  const isDoublePente = productType === 'double-pente';
  const isLambrequinSeul = productType === 'lambrequin-seul';
  const isPergola = productType === 'pergola';
  const isBriseVue = productType === 'brise-vue';
  const isToileAuMetre = productType === 'toile-au-metre';

  // Fonction pour calculer les jours fériés français
  const getJoursFeries = (year) => {
    // Calcul du lundi de Pâques
    const getPaques = (year) => {
      const a = year % 19;
      const b = Math.floor(year / 100);
      const c = year % 100;
      const d = Math.floor(b / 4);
      const e = b % 4;
      const f = Math.floor((b + 8) / 25);
      const g = Math.floor((b - f + 1) / 3);
      const h = (19 * a + b - d - g + 15) % 30;
      const i = Math.floor(c / 4);
      const k = c % 4;
      const l = (32 + 2 * e + 2 * i - h - k) % 7;
      const m = Math.floor((a + 11 * h + 22 * l) / 451);
      const month = Math.floor((h + l - 7 * m + 114) / 31);
      const day = ((h + l - 7 * m + 114) % 31) + 1;
      const paques = new Date(year, month - 1, day);
      const lundiPaques = new Date(paques);
      lundiPaques.setDate(paques.getDate() + 1);
      return lundiPaques;
    };

    const paques = getPaques(year);
    const lundiPaques = new Date(paques);
    lundiPaques.setDate(paques.getDate() + 1);
    
    const ascension = new Date(paques);
    ascension.setDate(paques.getDate() + 39);
    
    const lundiPentecote = new Date(paques);
    lundiPentecote.setDate(paques.getDate() + 50);

    return [
      new Date(year, 0, 1),   // 1er janvier
      lundiPaques,            // Lundi de Pâques
      new Date(year, 4, 1),   // 1er mai
      new Date(year, 4, 8),   // 8 mai
      ascension,              // Ascension
      lundiPentecote,         // Lundi de Pentecôte
      new Date(year, 6, 14),  // 14 juillet
      new Date(year, 7, 15),  // 15 août
      new Date(year, 10, 1),  // 1er novembre
      new Date(year, 10, 11), // 11 novembre
      new Date(year, 11, 25)  // 25 décembre
    ];
  };

  // Fonction pour vérifier si une date est un jour ouvré
  const isJourOuvre = (date) => {
    const day = date.getDay();
    // Weekend
    if (day === 0 || day === 6) return false;
    
    // Jours fériés
    const year = date.getFullYear();
    const joursFeries = getJoursFeries(year);
    
    return !joursFeries.some(ferie => 
      ferie.getDate() === date.getDate() &&
      ferie.getMonth() === date.getMonth() &&
      ferie.getFullYear() === date.getFullYear()
    );
  };

  // Fonction pour calculer la date d'expédition (10 jours ouvrés)
  const calculateShippingDate = () => {
    let date = new Date();
    let joursOuvresComptes = 0;
    
    // Si aujourd'hui n'est pas ouvré, commencer au prochain jour ouvré
    while (!isJourOuvre(date)) {
      date.setDate(date.getDate() + 1);
    }
    
    // Compter 10 jours ouvrés
    while (joursOuvresComptes < 10) {
      date.setDate(date.getDate() + 1);
      if (isJourOuvre(date)) {
        joursOuvresComptes++;
      }
    }
    
    // Formatter en français
    const mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `Expédition prévue le : ${date.getDate()} ${mois[date.getMonth()]} ${date.getFullYear()}`;
  };
  // Fonction pour gérer la navigation des catégories
  const handleCategoryClick = (categoryName) => {
    const routes = {
      "Toile De Store": "/",
      "Toile de Store Droit": "/store-droit",
      "Toile de Store à Double Pente": "/store-double-pente",
      "Lambrequin Seul": "/lambrequin",
      "Toile De Pergola": "/pergola",
      "Brise-Vue De Balcon": "/brise-vue",
      "Toile de Store au Metre": "/toile-au-metre"
    };
    
    const route = routes[categoryName];
    if (route) {
      navigate(route);
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        // Charger tous les produits et chercher par slug ou id
        const response = await axios.get(`${BACKEND_URL}/api/products/`);
        const foundProduct = response.data.find(p => p.slug === slug || p.id === slug);
        
        if (!foundProduct) {
          throw new Error('Produit non trouvé');
        }
        
        // Adapter le produit au format attendu
        const apiProduct = foundProduct;
        const adaptedProduct = {
          id: apiProduct.id,
          name: apiProduct.code1 || apiProduct.full_code || 'Produit',
          collection: apiProduct.gamme || 'N/A',
          brand: apiProduct.supplier || 'N/A',
          price: 0,
          image: apiProduct.image || null,
          slug: apiProduct.slug || apiProduct.id,
          thumbnail: apiProduct.thumbnail || apiProduct.image || null,
          description: apiProduct.characteristics || 'Aucune description disponible',
          supplier: apiProduct.supplier,
          code1: apiProduct.code1,
          // Tous les champs techniques de l'Excel
          ref_frs: apiProduct.ref_frs,
          category: apiProduct.category,
          code2: apiProduct.code2,
          code3: apiProduct.code3,
          subcat1: apiProduct.subcat1,
          subcat2: apiProduct.subcat2,
          subcat3: apiProduct.subcat3,
          acrylique: apiProduct.acrylique,
          teinte_masse: apiProduct.teinte_masse,
          auto_nettoyant: apiProduct.auto_nettoyant,
          deperlant: apiProduct.deperlant,
          anti_fongique: apiProduct.anti_fongique,
          etanche: apiProduct.etanche,
          waterproof: apiProduct.waterproof,
          warranty: apiProduct.warranty,
          width: apiProduct.width,
          motif: apiProduct.motif,
          weight: apiProduct.weight,
          epaisseur: apiProduct.epaisseur,
          protection_uv: apiProduct.protection_uv,
          uv: apiProduct.uv,
          conform_termique: apiProduct.conform_termique,
          transmission_solaire: apiProduct.transmission_solaire,
          reflexion_solaire: apiProduct.reflexion_solaire,
          absorption_solaire: apiProduct.absorption_solaire,
          protection_visuelle: apiProduct.protection_visuelle,
          visuel: apiProduct.visuel,
          gtot_exterieur: apiProduct.gtot_exterieur,
          gtot_interieur: apiProduct.gtot_interieur,
          taux_ouverture: apiProduct.taux_ouverture,
          ral: apiProduct.ral
        };
        
        setProduct(adaptedProduct);
        
        // Récupérer le prix au m²
        try {
          const priceResponse = await axios.get(`${BACKEND_URL}/api/calculate/price-per-m2/${apiProduct.id}`);
          setPricePerM2(priceResponse.data.price_per_m2);
        } catch (error) {
          console.error('Erreur lors du chargement du prix:', error);
          setPricePerM2(null);
        } finally {
          setLoadingPrice(false);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement du produit:', error);
        // Fallback sur mockData si l'API échoue
        const foundProduct = products.find(p => p.slug === slug);
        if (foundProduct) {
          setProduct(foundProduct);
        }
        setLoading(false);
        setLoadingPrice(false);
      }
    };
    
    fetchProduct();
  }, [slug]);

  // Calculer le coût de livraison minimum pour 1m² en point relais
  useEffect(() => {
    if (!product) return;
    
    const calculateMinDelivery = async () => {
      const weight = parseFloat(product.weight);
      
      if (!weight || isNaN(weight)) {
        console.log('Poids du produit non disponible, utilisation valeur par défaut 250g');
        // Valeur par défaut : 250g/m² (tissu moyen)
        const defaultWeight = 0.25; // kg
        
        try {
          const response = await axios.post(`${BACKEND_URL}/api/delivery/calculate?delivery_method_id=point-relais&weight_kg=${defaultWeight}`);
          if (response.data && response.data.cost_ttc) {
            setMinDeliveryCost(response.data.cost_ttc);
          }
        } catch (error) {
          console.error('Erreur calcul livraison:', error);
          setMinDeliveryCost(null);
        }
        return;
      }
      
      try {
        // Poids d'1m² en kg
        const weightPerM2 = weight / 1000; // Convertir grammes en kg
        
        // Récupérer le coût de livraison en point relais pour ce poids
        const response = await axios.post(`${BACKEND_URL}/api/delivery/calculate?delivery_method_id=point-relais&weight_kg=${weightPerM2}`);
        
        if (response.data && response.data.cost_ttc) {
          setMinDeliveryCost(response.data.cost_ttc);
        }
      } catch (error) {
        console.error('Erreur calcul livraison:', error);
        setMinDeliveryCost(null);
      }
    };
    
    calculateMinDelivery();
  }, [product?.id, BACKEND_URL]); // Ne dépend que de l'ID du produit

  if (loading || !product) {
    return <div className="min-h-screen flex items-center justify-center">Chargement du produit...</div>;
  }

  // Pictogrammes des caractéristiques de la toile
  const productFeatures = [
    { image: '/images/pictos/anti-uv.png', label: 'Protection Anti-UV' },
    { image: '/images/pictos/100-acrylique.png', label: 'Toile 100% Acrylique' },
    { image: '/images/pictos/anti-decoloration.png', label: 'Anti-Décoloration' },
    { image: '/images/pictos/anti-moisissures.png', label: 'Anti-Moisissures' },
    { image: '/images/pictos/anti-salissures.png', label: 'Anti-Salissures' },
    { image: '/images/pictos/etanche.png', label: 'Toile Étanche' }
  ];
  
  // Fonction pour gérer le clic sur un pictogramme
  const handlePictoClick = (index) => {
    if (activePictoIndex === index) {
      // Si on clique sur le picto déjà actif, on revient à l'image principale
      setActivePictoIndex(null);
    } else {
      // Sinon on affiche ce pictogramme
      setActivePictoIndex(index);
    }
  };
  
  // Navigation avec les flèches : image principale (index -1) + 6 pictos (index 0-5)
  const handlePrevImage = () => {
    if (activePictoIndex === null) {
      // De l'image principale, aller au dernier picto
      setActivePictoIndex(5);
    } else if (activePictoIndex === 0) {
      // Du premier picto, retourner à l'image principale
      setActivePictoIndex(null);
    } else {
      // Aller au picto précédent
      setActivePictoIndex(activePictoIndex - 1);
    }
  };
  
  const handleNextImage = () => {
    if (activePictoIndex === null) {
      // De l'image principale, aller au premier picto
      setActivePictoIndex(0);
    } else if (activePictoIndex === 5) {
      // Du dernier picto, retourner à l'image principale
      setActivePictoIndex(null);
    } else {
      // Aller au picto suivant
      setActivePictoIndex(activePictoIndex + 1);
    }
  };
  
  // Image à afficher (principale ou pictogramme)
  const currentDisplayImage = activePictoIndex !== null 
    ? productFeatures[activePictoIndex].image 
    : product.image;

  const relatedProducts = products.filter(p => 
    p.brand === product.brand && p.id !== product.id
  ).slice(0, 4);

  const handleConfigChange = (config) => {
    setConfiguration(config);
  };

  const handleAddToCart = () => {
    if (configuration && product) {
      const item = {
        product: product,
        configuration: configuration,
        totalPrice: configuration.totalPrice
      };
      addToCart(item);
      setCartItem(item);
      setShowCartModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Head avec métadonnées produit */}
      {product && (
        <SEOHead 
          title={`${product.name} - ${product.gamme || 'Toile Store'}`}
          description={`${product.name} - Toile pour store banne sur mesure. Ref: ${product.ref_frs}. ${product.gamme ? `Collection ${product.gamme}.` : ''} Fabrication française de qualité.`}
          url={`/produit/${slug}`}
          image={product.image}
          type="product"
          keywords={[product.name, product.gamme, product.supplier, product.ref_frs]}
          product={{
            name: product.name,
            description: `${product.name} - Toile pour store banne sur mesure confectionnée en France`,
            image: product.image,
            ref_frs: product.ref_frs,
            id: product.id,
            supplier: product.supplier
          }}
          breadcrumbs={[
            { name: 'Accueil', url: '/' },
            { name: product.gamme || 'Produits', url: '/' },
            { name: product.name, url: `/produit/${slug}` }
          ]}
        />
      )}
      
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-8">
            <Logo showTagline={true} clickable={true} />

            <div className="flex-1 max-w-2xl">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher un produit, une référence..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-celadon"
                />
                <button className="absolute right-0 top-0 bottom-0 bg-celadon text-white px-6 rounded-r-lg hover:bg-celadon-light transition-colors">
                  Chercher
                </button>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <button 
                onClick={() => navigate('/pro/login')}
                className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
                title="Espace PRO"
              >
                <User size={24} />
              </button>
              <button className="flex items-center gap-2 text-gray-700 hover:text-gray-900 relative">
                <ShoppingCart size={24} />
                <span className="absolute -top-2 -right-2 bg-celadon text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
                <span className="font-medium">PANIER</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-celadon text-white">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <ul className="flex items-center justify-center gap-2 mb-1">
            {navCategories.slice(0, 5).map((category, index) => (
              <li key={index}>
                <button 
                  onClick={() => handleCategoryClick(category.name)}
                  className="px-4 py-2 hover:bg-celadon-light transition-colors flex items-center gap-1 text-sm rounded"
                >
                  {category.name}
                </button>
              </li>
            ))}
          </ul>
          <ul className="flex items-center justify-center gap-2">
            {navCategories.slice(5, 9).map((category, index) => (
              <li key={index + 5}>
                <button 
                  onClick={() => handleCategoryClick(category.name)}
                  className="px-4 py-2 hover:bg-celadon-light transition-colors flex items-center gap-1 text-sm rounded"
                >
                  {category.name}
                </button>
              </li>
            ))}
            <li>
              <button 
                onClick={() => navigate('/devis-marquage-flocage')}
                className="px-4 py-2 hover:bg-celadon-light transition-colors text-sm rounded"
              >
                Marquage/Flocage
              </button>
            </li>
            <li>
              <button 
                onClick={() => navigate('/devis-marquage-lumineux-flocage')}
                className="px-4 py-2 hover:bg-celadon-light transition-colors text-sm rounded"
              >
                Marquage Lumineux/Flocage
              </button>
            </li>
            <li>
              <button 
                onClick={() => navigate('/demande-devis')}
                className="px-4 py-2 bg-celadon hover:bg-celadon-light transition-colors text-sm font-medium rounded ml-2"
              >
                DEVIS
              </button>
            </li>
            <li>
              <button 
                onClick={() => navigate('/pro/login')}
                className="px-4 py-2 hover:bg-celadon-light transition-colors text-sm rounded"
              >
                PRO
              </button>
            </li>
          </ul>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="text-sm text-gray-600">
            <span className="hover:text-gray-900 cursor-pointer" onClick={() => navigate('/')}>Accueil</span>
            <span className="mx-2">/</span>
            <span className="hover:text-gray-900 cursor-pointer" onClick={() => navigate('/')}>Toile de store banne</span>
            <span className="mx-2">/</span>
            <span className="text-gray-900">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 gap-12">
          {/* Left Column - Images */}
          <div>
            {/* Main Image */}
            <div className="relative mb-4 bg-gray-100 rounded-lg overflow-hidden group">
              <img
                src={currentDisplayImage}
                alt={activePictoIndex !== null ? productFeatures[activePictoIndex].label : product.name}
                className="w-full h-[500px] object-cover transition-opacity duration-300 ease-in-out"
                style={{ objectFit: activePictoIndex !== null ? 'contain' : 'cover' }}
              />
              {/* Image Navigation */}
              <button
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Thumbnail Images - Pictogrammes des Caractéristiques */}
            <div className="grid grid-cols-6 gap-2 mt-4">
              {productFeatures.map((feature, index) => (
                <button
                  key={index}
                  onClick={() => handlePictoClick(index)}
                  className={`border-2 rounded-lg overflow-hidden p-2 bg-white transition-all duration-300 cursor-pointer ${
                    activePictoIndex === index 
                      ? 'border-celadon ring-2 ring-celadon ring-offset-2 shadow-lg' 
                      : 'border-gray-200 hover:border-celadon hover:shadow-md'
                  }`}
                  title={feature.label}
                >
                  <img 
                    src={activePictoIndex === index ? product.image : feature.image}
                    alt={activePictoIndex === index ? product.name : feature.label}
                    className="w-full h-24 object-contain"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>

            {/* Product Features */}
            <div className="mt-8 grid grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Truck className="text-celadon flex-shrink-0" size={24} />
                <div>
                  <p className="font-medium text-sm">Livraison à partir de :</p>
                  <p className="text-xs text-gray-600">
                    {minDeliveryCost !== null 
                      ? `${formatPrice(minDeliveryCost)} € TTC`
                      : 'Calcul en cours...'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Shield className="text-celadon flex-shrink-0" size={24} />
                <div>
                  <p className="font-medium text-sm">Garantie 12 ans</p>
                  <p className="text-xs text-gray-600">Sur toutes les toiles des collections S&P™</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Clock className="text-celadon flex-shrink-0" size={24} />
                <div>
                  <p className="font-medium text-sm">{calculateShippingDate()}</p>
                  <p className="text-xs text-gray-600">10 jours ouvrés (hors week-ends et jours fériés)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Product Info */}
          <div>
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-1">{product.collection}</p>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
              <p className="text-sm text-gray-600">Référence : {product.slug}</p>
            </div>

            {/* Price */}
            <div className="mb-6 p-6 bg-gray-50 rounded-lg">
              {loadingPrice ? (
                <p className="text-4xl font-bold text-gray-400">Chargement...</p>
              ) : pricePerM2 === null || pricePerM2 === undefined ? (
                <p className="text-2xl font-bold text-gray-900">Prix sur demande</p>
              ) : (
                <p className="text-4xl font-bold text-celadon">À partir de {formatPrice(pricePerM2)} €</p>
              )}
            </div>

            {/* Configuration Form */}
            <ConfigurationForm 
              basePrice={product.price}
              productId={product.id}
              onConfigChange={handleConfigChange}
              isDoublePente={isDoublePente}
              isLambrequinSeul={isLambrequinSeul}
              isPergola={isPergola}
              isBriseVue={isBriseVue}
              isToileAuMetre={isToileAuMetre}
              productWidth={product.width}
            />

            {/* Add to Cart Button */}
            <button 
              onClick={handleAddToCart}
              className="w-full py-4 bg-celadon text-white rounded-lg font-bold text-lg hover:bg-celadon-light transition-colors flex items-center justify-center gap-2 mb-4 mt-6"
            >
              <ShoppingCart size={24} />
              Ajouter au panier
            </button>

            {/* Stock Status */}
            <div className="flex items-center gap-2 text-sm text-celadon">
              <Check size={18} />
              <span className="font-medium">En stock - {calculateShippingDate()}</span>
            </div>
          </div>
        </div>

        {/* Product Tabs */}
        <div className="mt-16">
          <div className="border-b border-gray-200">
            <div className="flex gap-8">
              <button
                onClick={() => setSelectedTab('description')}
                className={`pb-4 px-2 font-medium transition-colors ${
                  selectedTab === 'description'
                    ? 'text-celadon border-b-2 border-celadon'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Description
              </button>
              <button
                onClick={() => setSelectedTab('caracteristiques')}
                className={`pb-4 px-2 font-medium transition-colors ${
                  selectedTab === 'caracteristiques'
                    ? 'text-celadon border-b-2 border-celadon'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Caractéristiques
              </button>
              <button
                onClick={() => setSelectedTab('livraison')}
                className={`pb-4 px-2 font-medium transition-colors ${
                  selectedTab === 'livraison'
                    ? 'text-celadon border-b-2 border-celadon'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Livraison
              </button>
            </div>
          </div>

          <div className="py-8">
            {selectedTab === 'description' && (
              <div className="prose max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {product.description || 'Aucune description disponible pour ce produit.'}
                </div>
              </div>
            )}

            {selectedTab === 'caracteristiques' && (
              <div className="space-y-6">
                {/* Informations Générales */}
                <div>
                  <h4 className="font-bold text-lg mb-4 text-gray-900">Informations Générales</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {product.ref_frs && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Référence fournisseur:</span>
                        <span className="font-medium text-gray-900">{product.ref_frs}</span>
                      </div>
                    )}
                    {product.brand && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Marque:</span>
                        <span className="font-medium text-gray-900">{product.brand}</span>
                      </div>
                    )}
                    {product.collection && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Collection:</span>
                        <span className="font-medium text-gray-900">{product.collection}</span>
                      </div>
                    )}
                    {product.category && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Catégorie:</span>
                        <span className="font-medium text-gray-900">{product.category}</span>
                      </div>
                    )}
                    {product.motif && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Motif:</span>
                        <span className="font-medium text-gray-900">{product.motif}</span>
                      </div>
                    )}
                    {product.ral && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">RAL:</span>
                        <span className="font-medium text-gray-900">{product.ral}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dimensions & Propriétés Physiques */}
                <div>
                  <h4 className="font-bold text-lg mb-4 text-gray-900">Dimensions & Propriétés</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {product.width && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Largeur:</span>
                        <span className="font-medium text-gray-900">{product.width}</span>
                      </div>
                    )}
                    {product.weight && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Poids:</span>
                        <span className="font-medium text-gray-900">{product.weight}</span>
                      </div>
                    )}
                    {product.epaisseur && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Épaisseur:</span>
                        <span className="font-medium text-gray-900">{product.epaisseur}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Composition & Traitements */}
                <div>
                  <h4 className="font-bold text-lg mb-4 text-gray-900">Composition & Traitements</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {product.acrylique && (
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-green-600">✓</span>
                        <span className="text-gray-900">Acrylique</span>
                      </div>
                    )}
                    {product.teinte_masse && (
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-green-600">✓</span>
                        <span className="text-gray-900">Teintée dans la masse</span>
                      </div>
                    )}
                    {product.auto_nettoyant && (
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-green-600">✓</span>
                        <span className="text-gray-900">Auto-nettoyant</span>
                      </div>
                    )}
                    {product.deperlant && (
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-green-600">✓</span>
                        <span className="text-gray-900">Déperlant</span>
                      </div>
                    )}
                    {product.anti_fongique && (
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-green-600">✓</span>
                        <span className="text-gray-900">Anti-fongique</span>
                      </div>
                    )}
                    {product.etanche && (
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-green-600">✓</span>
                        <span className="text-gray-900">Étanche</span>
                      </div>
                    )}
                    {product.waterproof && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Waterproof:</span>
                        <span className="font-medium text-gray-900">{product.waterproof}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Protection UV & Solaire */}
                <div>
                  <h4 className="font-bold text-lg mb-4 text-gray-900">Protection UV & Solaire</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {product.protection_uv && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Protection UV:</span>
                        <span className="font-medium text-gray-900">{product.protection_uv}</span>
                      </div>
                    )}
                    {product.uv && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">UV:</span>
                        <span className="font-medium text-gray-900">{product.uv}</span>
                      </div>
                    )}
                    {product.transmission_solaire && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Transmission solaire:</span>
                        <span className="font-medium text-gray-900">{product.transmission_solaire}</span>
                      </div>
                    )}
                    {product.reflexion_solaire && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Réflexion solaire:</span>
                        <span className="font-medium text-gray-900">{product.reflexion_solaire}</span>
                      </div>
                    )}
                    {product.absorption_solaire && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Absorption solaire:</span>
                        <span className="font-medium text-gray-900">{product.absorption_solaire}</span>
                      </div>
                    )}
                    {product.gtot_exterieur && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Gtot extérieur:</span>
                        <span className="font-medium text-gray-900">{product.gtot_exterieur}</span>
                      </div>
                    )}
                    {product.gtot_interieur && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Gtot intérieur:</span>
                        <span className="font-medium text-gray-900">{product.gtot_interieur}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Performance Thermique & Visuelle */}
                <div>
                  <h4 className="font-bold text-lg mb-4 text-gray-900">Performance Thermique & Visuelle</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {product.conform_termique && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Confort thermique:</span>
                        <span className="font-medium text-gray-900">{product.conform_termique}</span>
                      </div>
                    )}
                    {product.protection_visuelle && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Protection visuelle:</span>
                        <span className="font-medium text-gray-900">{product.protection_visuelle}</span>
                      </div>
                    )}
                    {product.visuel && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Visuel:</span>
                        <span className="font-medium text-gray-900">{product.visuel}</span>
                      </div>
                    )}
                    {product.taux_ouverture && (
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Taux d'ouverture:</span>
                        <span className="font-medium text-gray-900">{product.taux_ouverture}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Garantie */}
                {product.warranty && (
                  <div>
                    <h4 className="font-bold text-lg mb-4 text-gray-900">Garantie</h4>
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-gray-900">{product.warranty}</p>
                    </div>
                  </div>
                )}

                {/* Sous-catégories (si présentes) */}
                {(product.subcat1 || product.subcat2 || product.subcat3) && (
                  <div>
                    <h4 className="font-bold text-lg mb-4 text-gray-900">Classifications</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {product.subcat1 && (
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Sous-catégorie 1:</span>
                          <span className="font-medium text-gray-900">{product.subcat1}</span>
                        </div>
                      )}
                      {product.subcat2 && (
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Sous-catégorie 2:</span>
                          <span className="font-medium text-gray-900">{product.subcat2}</span>
                        </div>
                      )}
                      {product.subcat3 && (
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="text-gray-600">Sous-catégorie 3:</span>
                          <span className="font-medium text-gray-900">{product.subcat3}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedTab === 'livraison' && (
              <div className="space-y-4 text-gray-700">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-bold mb-2">📦 Délai de livraison</h4>
                  <p>{calculateShippingDate()} (10 jours ouvrés, hors week-ends et jours fériés français).</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-bold mb-2">🚚 Frais de port</h4>
                  <p>Livraison offerte dès 150€ d'achat. Sinon, 9,90€ en France métropolitaine.</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-bold mb-2">🔄 Retours</h4>
                  <p>Retours gratuits sous 30 jours. La toile doit être non découpée et dans son emballage d'origine.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold mb-6">Produits similaires</h2>
            <div className="grid grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <div
                  key={relatedProduct.id}
                  onClick={() => navigate(`/produit/${relatedProduct.slug}`)}
                  className="bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-all cursor-pointer"
                >
                  <img
                    src={relatedProduct.image}
                    alt={relatedProduct.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <p className="text-xs text-gray-500 mb-1">{relatedProduct.collection}</p>
                    <h3 className="font-medium text-gray-900 mb-2 text-sm line-clamp-2">
                      {relatedProduct.name}
                    </h3>
                    <p className="text-lg font-bold text-gray-900">{formatPrice(relatedProduct.price)} €</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cart Modal */}
      <CartModal 
        isOpen={showCartModal}
        onClose={() => setShowCartModal(false)}
        cartItem={cartItem}
      />

      <Footer />
    </div>
  );
};

export default ProductDetail;